import { create } from 'zustand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Timer persistence keys
const TIMER_STORAGE_KEY = 'tracker_active_timer';

export const useAppStore = create((set, get) => ({
  // Organizations
  organizations: [],
  currentOrg: null,

  // Projects
  projects: [],
  currentProject: null,

  // Tasks
  tasks: [],
  selectedTask: null,

  // Bookmarks
  bookmarks: [],

  // Filters
  filters: {
    assignee: 'self', // 'all', 'self', or user_id
    priority: 'all', // 'all', 'low', 'medium', 'high', 'urgent'
  },

  // Timer
  activeTimer: null,
  timerTask: null,
  elapsedSeconds: 0,
  timerInterval: null,

  // UI State
  sidebarCollapsed: false,
  isLoading: false,

  // Fetch helpers
  fetchWithAuth: async (url, options = {}) => {
    const { useAuthStore } = await import('./authStore');
    const token = useAuthStore.getState().token;
    const res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data.detail || 'Request failed');
    }
    return data;
  },

  // Sidebar
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

  // Filters
  setFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),

  // Organizations
  fetchOrganizations: async () => {
    set({ isLoading: true });
    try {
      const data = await get().fetchWithAuth(`${API}/organizations`);
      set({ organizations: data, isLoading: false });
      if (data.length > 0 && !get().currentOrg) {
        set({ currentOrg: data[0] });
      }
      return data;
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  createOrganization: async (name, description = '') => {
    const data = await get().fetchWithAuth(`${API}/organizations`, {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
    set((state) => ({ organizations: [...state.organizations, data], currentOrg: data }));
    return data;
  },

  setCurrentOrg: (org) => {
    set({ currentOrg: org, currentProject: null, projects: [], tasks: [], selectedTask: null });
  },

  // Projects
  fetchProjects: async (orgId) => {
    set({ isLoading: true });
    try {
      const data = await get().fetchWithAuth(`${API}/projects?org_id=${orgId}`);
      set({ projects: data, isLoading: false });
      return data;
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  createProject: async (name, description, orgId) => {
    const data = await get().fetchWithAuth(`${API}/projects`, {
      method: 'POST',
      body: JSON.stringify({ name, description, org_id: orgId }),
    });
    set((state) => ({ projects: [...state.projects, data] }));
    return data;
  },

  updateProject: async (projectId, updates) => {
    const data = await get().fetchWithAuth(`${API}/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    set((state) => ({
      projects: state.projects.map((p) => (p.id === projectId ? data : p)),
      currentProject: state.currentProject?.id === projectId ? data : state.currentProject,
    }));
    return data;
  },

  setCurrentProject: (project) => {
    set({ currentProject: project, tasks: [], selectedTask: null });
  },

  // Tasks
  fetchAssignedTasks: async (orgId) => {
    return get().fetchWithAuth(`${API}/tasks/assigned?org_id=${orgId}`);
  },

  fetchTasks: async (projectId, filters = {}) => {
    set({ isLoading: true });
    try {
      const { useAuthStore } = await import('./authStore');
      const userId = useAuthStore.getState().user?.id;
      const currentFilters = { ...get().filters, ...filters };

      let url = `${API}/tasks?project_id=${projectId}`;
      if (filters.assignee === 'self' && userId) {
        url += `&my_tasks=true`;
      } else if (filters.assignee && filters.assignee !== 'all') {
        url += `&assignee=${filters.assignee}`;
      }
      if (filters.priority && filters.priority !== 'all') {
        url += `&priority=${filters.priority}`;
      }

      const data = await get().fetchWithAuth(url);
      set({ tasks: data, isLoading: false });
      return data;
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  createTask: async (taskData) => {
    const data = await get().fetchWithAuth(`${API}/tasks`, {
      method: 'POST',
      body: JSON.stringify(taskData),
    });
    set((state) => ({ tasks: [...state.tasks, data] }));
    return data;
  },

  updateTask: async (taskId, updates) => {
    const data = await get().fetchWithAuth(`${API}/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    set((state) => ({
      tasks: state.tasks.map((t) => (t.id === taskId ? data : t)),
      selectedTask: state.selectedTask?.id === taskId ? data : state.selectedTask,
    }));
    return data;
  },

  deleteTask: async (taskId) => {
    await get().fetchWithAuth(`${API}/tasks/${taskId}`, { method: 'DELETE' });
    set((state) => ({
      tasks: state.tasks.filter((t) => t.id !== taskId),
      selectedTask: state.selectedTask?.id === taskId ? null : state.selectedTask,
    }));
  },

  moveTask: async (taskId, newStatus, newOrder) => {
    // Store the original task for rollback if needed
    const originalTasks = get().tasks;
    const taskToMove = originalTasks.find((t) => t.id === taskId);

    if (!taskToMove) {
      throw new Error('Task not found');
    }

    // Optimistic update: immediately update UI
    set((state) => ({
      tasks: state.tasks.map((t) =>
        t.id === taskId
          ? { ...t, status: newStatus, column_order: newOrder }
          : t
      ),
    }));

    try {
      // Make the API call
      const data = await get().fetchWithAuth(`${API}/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus, column_order: newOrder }),
      });

      // Update with actual server response
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === taskId ? data : t)),
      }));

      return data;
    } catch (error) {
      // Rollback on error
      set({ tasks: originalTasks });
      throw error;
    }
  },

  setSelectedTask: (task) => set({ selectedTask: task }),

  // Comments
  fetchComments: async (taskId) => {
    return get().fetchWithAuth(`${API}/comments?task_id=${taskId}`);
  },

  createComment: async (taskId, content) => {
    return get().fetchWithAuth(`${API}/comments`, {
      method: 'POST',
      body: JSON.stringify({ task_id: taskId, content }),
    });
  },

  deleteComment: async (commentId) => {
    return get().fetchWithAuth(`${API}/comments/${commentId}`, { method: 'DELETE' });
  },

  // Activity
  fetchActivityLogs: async (taskId) => {
    return get().fetchWithAuth(`${API}/activity?task_id=${taskId}`);
  },

  // Timer with localStorage persistence
  initializeTimer: async () => {
    // Try to restore timer from localStorage
    const stored = localStorage.getItem(TIMER_STORAGE_KEY);
    if (stored) {
      try {
        const timerData = JSON.parse(stored);
        const startTime = new Date(timerData.startTime);
        const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
        set({
          activeTimer: timerData.timelog,
          timerTask: timerData.task,
          elapsedSeconds: elapsed
        });
        get().startTimerInterval();
      } catch (e) {
        localStorage.removeItem(TIMER_STORAGE_KEY);
      }
    }
    // Also verify with server
    await get().fetchActiveTimer();
  },

  fetchActiveTimer: async () => {
    try {
      const data = await get().fetchWithAuth(`${API}/timelogs/active`);
      if (data.timelog) {
        const startTime = new Date(data.timelog.start_time);
        const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
        set({ activeTimer: data.timelog, timerTask: data.task, elapsedSeconds: elapsed });
        // Store in localStorage for persistence
        localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify({
          timelog: data.timelog,
          task: data.task,
          startTime: data.timelog.start_time,
        }));
        get().startTimerInterval();
      } else {
        set({ activeTimer: null, timerTask: null, elapsedSeconds: 0 });
        localStorage.removeItem(TIMER_STORAGE_KEY);
        get().stopTimerInterval();
      }
      return data;
    } catch (err) {
      console.error('Error fetching active timer:', err);
      return null;
    }
  },

  startTimer: async (taskId) => {
    const data = await get().fetchWithAuth(`${API}/timelogs/start`, {
      method: 'POST',
      body: JSON.stringify({ task_id: taskId }),
    });
    const task = get().tasks.find((t) => t.id === taskId);
    set({ activeTimer: data, timerTask: task || null, elapsedSeconds: 0 });
    // Store in localStorage
    localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify({
      timelog: data,
      task: task,
      startTime: data.start_time,
    }));
    get().startTimerInterval();
    return data;
  },

  stopTimer: async () => {
    const data = await get().fetchWithAuth(`${API}/timelogs/stop`, { method: 'POST' });
    get().stopTimerInterval();
    set({ activeTimer: null, timerTask: null, elapsedSeconds: 0 });
    localStorage.removeItem(TIMER_STORAGE_KEY);
    // Refresh tasks to update total time
    const currentProject = get().currentProject;
    if (currentProject) {
      get().fetchTasks(currentProject.id);
    }
    return data;
  },

  startTimerInterval: () => {
    const existing = get().timerInterval;
    if (existing) clearInterval(existing);

    // Calculate elapsed from stored start time for accuracy
    const stored = localStorage.getItem(TIMER_STORAGE_KEY);
    if (stored) {
      const timerData = JSON.parse(stored);
      const startTime = new Date(timerData.startTime);
      const updateTimer = () => {
        const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
        set({ elapsedSeconds: elapsed });
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      set({ timerInterval: interval });
    }
  },

  stopTimerInterval: () => {
    const interval = get().timerInterval;
    if (interval) {
      clearInterval(interval);
      set({ timerInterval: null });
    }
  },

  // Reports
  fetchTimeSummary: async (orgId, filters = {}) => {
    const params = new URLSearchParams({ org_id: orgId });
    if (filters.projectId) params.append('project_id', filters.projectId);
    if (filters.userId) params.append('user_id', filters.userId);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    return get().fetchWithAuth(`${API}/reports/time-summary?${params}`);
  },

  fetchMemberSummary: async (orgId, filters = {}) => {
    const params = new URLSearchParams({ org_id: orgId });
    if (filters.projectId) params.append('project_id', filters.projectId);
    if (filters.userId) params.append('user_id', filters.userId);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    return get().fetchWithAuth(`${API}/reports/member-summary?${params}`);
  },

  // Time logs for task
  fetchTaskTimeLogs: async (taskId) => {
    return get().fetchWithAuth(`${API}/timelogs?task_id=${taskId}`);
  },

  // Members
  fetchOrgMembers: async (orgId) => {
    return get().fetchWithAuth(`${API}/organizations/${orgId}/members`);
  },

  addOrgMember: async (orgId, email, role) => {
    return get().fetchWithAuth(`${API}/organizations/${orgId}/members?email=${encodeURIComponent(email)}&role=${role}`, {
      method: 'POST',
    });
  },

  fetchProjectMembers: async (projectId) => {
    return get().fetchWithAuth(`${API}/projects/${projectId}/members`);
  },

  addProjectMember: async (projectId, email, role) => {
    return get().fetchWithAuth(`${API}/projects/${projectId}/members?email=${encodeURIComponent(email)}&role=${role}`, {
      method: 'POST',
    });
  },

  // Bookmarks
  fetchBookmarks: async (projectId) => {
    set({ isLoading: true });
    try {
      const data = await get().fetchWithAuth(`${API}/bookmarks?project_id=${projectId}`);
      set({ bookmarks: data, isLoading: false });
      return data;
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  createBookmark: async (bookmarkData) => {
    const data = await get().fetchWithAuth(`${API}/bookmarks`, {
      method: 'POST',
      body: JSON.stringify(bookmarkData),
    });
    set((state) => ({ bookmarks: [...state.bookmarks, data] }));
    return data;
  },

  deleteBookmark: async (bookmarkId) => {
    await get().fetchWithAuth(`${API}/bookmarks/${bookmarkId}`, { method: 'DELETE' });
    set((state) => ({
      bookmarks: state.bookmarks.filter((b) => b.id !== bookmarkId),
    }));
  },
}));
